import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  computed,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime, forkJoin, of, from } from 'rxjs';
import { PurchaseOrderService } from '../services/purchase-order.service';
import { PurchaseRequestService } from '../../purchase-request/purchase-request.service';
import { VendorService } from '../../vendor/services/vendor.service';
import { PurchaseOrderDTO } from '@app/api/purchase-service/model/purchaseOrderDTO';
import { GoodsSkuControllerService } from '@app/api/goods-service/api/api';
import { GoodsSkuPageVO } from '@app/api/goods-service/model/models';
import { PurchaseOrderConfigService } from '@app/feature/configuration/order/purchase-order-config/services/purchase-order-config.service';
import { applyPurchaseOrderConfigToFields } from '../po.helper';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  AddFormComponent,
  FormField,
  ActionButton,
  TableSection,
} from '@shared/components/add-form/add-form.component';
import {
  PURCHASE_ORDER_ADD_FORM_FIELDS,
  PURCHASE_ORDER_ADD_FORM_FIELDS_PR,
  PURCHASE_ORDER_HEADER_ACTIONS,
  PURCHASE_ORDER_DETAIL_TABLE_COLUMNS,
  PurchaseOrderDetailItem,
} from './purchase-order-add.config';
import { PurchaseRequestRecord } from '../../purchase-request/purchase-request.config';
import { PurchaseRequestItemDialogComponent } from '../../components/selected-sku-dialog/selected-sku-dialog.component';
import { PurchaseRequestItem } from '../../purchase-request/models/purchase-request-item';
import { VendorSkuControllerService } from '@app/api/vendor-service/api/api';
import { map, catchError, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  PageInfoVendorSkuDTO,
  VendorSkuDTO,
} from '@app/api/vendor-service/model/models';
import { PurchaseOrderItemDTO } from '@app/api/purchase-service/model/models';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import {
  classifyPurchaseOrderSaveError,
  normalizeDeliveryDateField,
  extractPurchaseRequestId as extractPurchaseRequestIdFromOptions,
  shouldSwitchToNormalMode as shouldSwitchToNormalModePure,
  shouldSwitchToPRMode as shouldSwitchToPRModePure,
  evaluatePurchaseOrderAddValidation,
  hasDetailUnitPriceBelowMinimum,
} from './purchase-order-add-shared';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import { ProcurementDropdownService } from '../../services/procurement-dropdown.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { VendorOptionsService } from '@shared/services/vendor-options.service';
import { withPaginatedFormFields } from '@shared/utils/paginated-field.helpers';

@Component({
  selector: 'app-purchase-order-add',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AddFormComponent,
    PurchaseRequestItemDialogComponent,
    ButtonModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './purchase-order-add.component.html',
  styleUrl: './purchase-order-add.component.css',
})
export class PurchaseOrderAddComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly purchaseOrderService = inject(PurchaseOrderService);
  private readonly purchaseRequestService = inject(PurchaseRequestService);
  private readonly vendorService = inject(VendorService);
  private readonly vendorSkuService = inject(VendorSkuControllerService);
  private readonly goodsSkuController = inject(GoodsSkuControllerService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly purchaseOrderConfigService = inject(
    PurchaseOrderConfigService,
  );
  private readonly procurementDropdownService = inject(
    ProcurementDropdownService,
  );
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly vendorOptionsService = inject(VendorOptionsService);
  private readonly destroy$ = new Subject<void>();

  // Form configuration
  formFields = signal<FormField[]>(PURCHASE_ORDER_ADD_FORM_FIELDS);
  headerActions = signal<ActionButton[]>(PURCHASE_ORDER_HEADER_ACTIONS);

  // Data
  detailList = signal<PurchaseOrderDetailItem[]>([]);
  vendorSKUList = signal<VendorSkuDTO[]>([]);
  selectedDetails = signal<PurchaseOrderDetailItem[]>([]);
  loading = signal(false);
  currentFormData: any = null;
  private skipCanDeactivate = false;
  @ViewChild('addForm') private readonly addFormComponent?: AddFormComponent;

  // System SKU map: skuCode -> SKU info (for mapping with vendor SKU)
  systemSkuMap = signal<Map<string, GoodsSkuPageVO>>(new Map());

  // Purchase request mode
  isPurchaseRequestMode = signal(false);
  purchaseRequestId: number | null = null;
  purchaseRequestData = signal<PurchaseRequestRecord | null>(null);
  private isPurchaseRequestLockedFromRoute = false;

  // Normal mode
  isNormalMode = signal(false);

  // Purchase request options for dropdown
  purchaseRequestOptions = signal<any[]>([]);
  // Vendor options for dropdown
  vendorOptions = signal<any[]>([]);
  warehouseOptions = signal<Array<{ label: string; value: string }>>([]);

  // Computed table sections that automatically update with data
  tableSections = computed(() => {
    const warehouseOptions = this.warehouseOptions();
    const defaultCols = PURCHASE_ORDER_DETAIL_TABLE_COLUMNS;

    const updateWarehouseColumn = (columns: TableColumn[]) => {
      return columns.map((col) => {
        if (col.prop === 'warehouseCode') {
          return {
            ...col,
            type: 'select' as const,
            options: warehouseOptions.map((opt) => ({
              label: opt.label,
              value: opt.value,
            })),
          };
        }
        if (col.prop === 'quantity' && !this.isNormalMode()) {
          return {
            ...col,
            type: 'text' as const,
            editable: false,
          };
        }
        return col;
      });
    };

    const columns = updateWarehouseColumn(defaultCols);

    if (this.isNormalMode()) {
      // Normal mode - use table section with actions
      const section: TableSection = {
        title: 'purchase_order.add.sections.detail_items',
        show: true,
        data: this.detailList(),
        columns: columns,
        actions: [
          {
            label: 'common.actions.delete',
            action: 'deleteSelectedDetail',
            severity: 'danger' as const,
            outlined: true,
            disabled: this.selectedDetails().length === 0,
            icon: 'delete',
          },
          {
            label: 'common.actions.new',
            action: 'addDetail',
            severity: 'info' as const,
            icon: 'add',
            disabled: !this.currentFormData?.vendorCode,
          },
        ],
        hasCheckbox: true,
      };
      return [section];
    } else {
      // Purchase request mode - use table section without actions
      const section: TableSection = {
        title: 'purchase_order.add.sections.detail_items',
        show: true,
        data: this.detailList(),
        columns: columns,
        actions: [],
        hasCheckbox: false,
      };
      return [section];
    }
  });

  // SKU selection dialog for normal mode
  showSkuDialog = signal(false);
  editingSkuItem = signal<PurchaseRequestItem | null>(null);

  // Unit price validation state
  private readonly unitPriceInput$ = new Subject<{
    id: string;
    value: number;
  }>();
  private readonly invalidUnitPriceIds = new Set<string>();

  ngOnInit(): void {
    this.checkPurchaseRequestMode();
    // Only initialize form for normal mode here
    // For PR mode, form will be initialized after data is loaded
    if (!this.isPurchaseRequestMode()) {
      this.initializeForm();
    }

    this.loadWarehouseOptionsForTable();
    this.loadAllSystemSkus(); // Load all system SKUs for mapping

    // Debounced realtime validation for unit price
    this.unitPriceInput$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(({ id, value }) => {
        if (value !== null && value !== undefined && value < 100) {
          this.invalidUnitPriceIds.add(id);
        } else {
          this.invalidUnitPriceIds.delete(id);
        }
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all system SKUs for mapping with vendor SKUs
   */
  private loadAllSystemSkus(): void {
    this.goodsSkuController
      .getList3(
        undefined, // keyword
        undefined, // spuName
        undefined, // spuCode
        undefined, // spuType
        0, // setFlag (ordinary SKUs)
        undefined, // categoryIdList
        [3], // status: 3-Effective (active SKUs only)
        undefined, // skuName
        undefined, // searchMode
        undefined, // skuCode
        undefined, // startDate
        undefined, // endDate
        undefined, // skuIds
        undefined, // lastUpdatedAfter
        undefined, // channelShopId
        undefined, // warehouseCode
        1, // showFlag - only Active SKUs can be mapped to a new purchase order
        0, // page
        10000, // large size to get all SKUs
        undefined, // sortBy
        undefined, // ascending
      )
      .pipe(
        switchMap((response: any) => {
          // Handle blob response
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return from(Promise.resolve(response));
        }),
        map((response: any) => {
          const pageContent = BlobConverterUtil.extractPageContent(
            response,
            [],
          );
          this.buildSystemSkuMap(pageContent);
        }),
        catchError((error) => {
          console.error('Error loading all system SKUs:', error);
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  /**
   * Build map of skuCode -> SKU info
   */
  private buildSystemSkuMap(skus: any[]): void {
    const skuMap = new Map<string, GoodsSkuPageVO>();
    skus.forEach((sku: any) => {
      if (sku.skuCode) {
        skuMap.set(sku.skuCode, sku);
      }
    });
    this.systemSkuMap.set(skuMap);
  }

  /**
   * Get system SKU info by skuCode
   */
  private getSystemSkuInfo(skuCode: string): GoodsSkuPageVO | undefined {
    return this.systemSkuMap().get(skuCode);
  }

  private applyPaginatedToFields(fields: FormField[]): FormField[] {
    return withPaginatedFormFields(fields, {
      purchaseRequest: {
        loadPage: (params) =>
          this.procurementDropdownService.getPurchaseRequestOptionsPage(params),
      },
      vendorCode: {
        loadPage: (params) =>
          this.vendorOptionsService.getOptionsPage(params, { valueKey: 'code' }),
      },
    });
  }

  private loadAndApplyPurchaseOrderConfig(): void {
    const config =
      this.purchaseOrderConfigService.getPurchaseOrderFieldsFromStorage();
    if (config && config.length > 0) {
      this.formFields.set(
        this.applyPaginatedToFields(
          applyPurchaseOrderConfigToFields(config, this.formFields()),
        ),
      );
      return;
    }
    this.purchaseOrderConfigService
      .getPurchaseOrderFields()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fields) => {
          this.formFields.set(
            this.applyPaginatedToFields(
              applyPurchaseOrderConfigToFields(fields, this.formFields()),
            ),
          );
        },
        error: (err) => {
          console.error('Error loading purchase order config:', err);
        },
      });
  }

  private checkPurchaseRequestMode(): void {
    const purchaseRequestIdParam =
      this.route.snapshot.queryParamMap.get('purchaseRequestId');
    if (purchaseRequestIdParam) {
      // Purchase Request Mode
      this.purchaseRequestId = Number(purchaseRequestIdParam);
      this.isPurchaseRequestLockedFromRoute = true;
      this.isPurchaseRequestMode.set(true);
      this.isNormalMode.set(false);

      // Set form fields for purchase request mode
      this.formFields.set(
        this.applyPaginatedToFields(PURCHASE_ORDER_ADD_FORM_FIELDS_PR),
      );
      // Load and apply config after setting form fields
      this.loadAndApplyPurchaseOrderConfig();
      this.loadPurchaseRequestData();
    } else {
      // Normal Mode
      this.isPurchaseRequestLockedFromRoute = false;
      this.isPurchaseRequestMode.set(false);
      this.isNormalMode.set(true);
      this.formFields.set(
        this.applyPaginatedToFields(PURCHASE_ORDER_ADD_FORM_FIELDS),
      );
      // Load and apply config after setting form fields
      this.loadAndApplyPurchaseOrderConfig();
      this.initializeNormalMode();
    }
  }

  private loadPurchaseRequestData(): void {
    if (!this.purchaseRequestId) return;

    this.loading.set(true);
    this.purchaseRequestService
      .getPurchaseRequestById(this.purchaseRequestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            this.purchaseRequestData.set(response);

            // Update form data with purchase request ID (keep as ID for dropdown)
            this.currentFormData = {
              ...this.currentFormData,
              purchaseRequest: this.purchaseRequestId, // Keep as ID for dropdown
              vendorCode: '', // Will be auto-selected by loadVendorOptions
              description: response.description || '',
            };

            // Load items from PR (this will call loadVendorOptions which will auto-select vendor)
            this.loadItemsFromPurchaseRequest(response);

            // Update form fields with PR options
            this.updatePurchaseRequestOptionsForForm(response);

            // For URL mode, disable the field and show as text (read-only)
            this.updatePurchaseRequestFieldForDisplay(response);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: String(
                this.translateService.instant('common.messages.error'),
              ),
              detail: this.translateService.instant(
                'purchase_order.add.messages.purchase_request_not_found',
              ),
            });
            this.router.navigate(['/procurement/purchase-order']);
          }
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: String(
              this.translateService.instant('common.messages.error'),
            ),
            detail: this.translateService.instant(
              'purchase_order.add.messages.failed_to_load_purchase_request',
            ),
          });
          this.router.navigate(['/procurement/purchase-order']);
        },
      });
  }

  private loadItemsFromPurchaseRequest(prData: PurchaseRequestRecord): void {
    if (!prData.items || prData.items.length === 0) {
      // Clear items if PR has no items
      this.detailList.set([]);
      return;
    }

    // Generate unique IDs using timestamp to avoid conflicts when reloading
    const timestamp = Date.now();
    const items: PurchaseOrderDetailItem[] = prData.items.map((item, index) => {
      // Map warehouse name from warehouse code
      const warehouseCode = item.warehouseCode || '';
      const warehouseName = warehouseCode
        ? this.getWarehouseName(warehouseCode)
        : '';
      return {
        id: `item-${timestamp}-${index}`,
        skuCode: item.skuCode || '',
        skuName: item.skuName || '',
        spuCode: item.spuCode || '',
        spuName: item.spuName || '',
        quantity: item.quantity || 0,
        unit: 'Cái',
        unitPrice: 0,
        tax: 0,
        totalValue: 0,
        vendorSkuCode: '',
        vendorSkuName: '',
        purchaseRequestItem: item.id,
        warehouseCode: warehouseCode,
        warehouseName: warehouseName,
      };
    });
    this.detailList.set(items);
    // Load vendor options for the dropdown
    this.loadVendorOptions();
  }

  /**
   * Reload full PR items when vendor is cleared
   */
  private reloadFullPRItems(): void {
    if (!this.purchaseRequestData()) {
      return;
    }

    const prData = this.purchaseRequestData()!;
    const timestamp = Date.now();
    const items: PurchaseOrderDetailItem[] = prData.items.map((item, index) => {
      const warehouseCode = item.warehouseCode || '';
      const warehouseName = warehouseCode
        ? this.getWarehouseName(warehouseCode)
        : '';
      return {
        id: `item-${timestamp}-${index}`,
        skuCode: item.skuCode || '',
        skuName: item.skuName || '',
        spuCode: item.spuCode || '',
        spuName: item.spuName || '',
        quantity: item.quantity || 0,
        unit: 'Cái',
        unitPrice: 0,
        tax: 0,
        totalValue: 0,
        vendorSkuCode: '', // Clear vendor SKU info
        vendorSkuName: '', // Clear vendor SKU name
        purchaseRequestItem: item.id,
        warehouseCode: warehouseCode,
        warehouseName: warehouseName,
      };
    });
    this.detailList.set(items);
    // Clear vendor SKU list
    this.vendorSKUList.set([]);
  }

  private getWarehouseName(warehouseCode: string): string {
    const warehouse = this.warehouseOptions().find(
      (wh) => wh.value === warehouseCode,
    );
    return warehouse?.label?.split(' - ')[1] || warehouseCode;
  }

  private initializeNormalMode(): void {
    // Set default delivery date to today for normal mode
    const today = new Date().toISOString().split('T')[0];
    this.currentFormData = {
      series: '',
      vendorCode: '',
      deliveryDate: today,
      description: '',
    };

    this.loadWarehouseOptionsForTable();
  }

  private loadPurchaseRequestOptions(): void {
    // Load purchase requests for the dropdown
    this.purchaseRequestService
      .getPurchaseRequestRecords({
        page: 0,
        size: 100,
        sortBy: 'id',
        ascending: false,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.pageContent) {
            // Filter to only include APPROVED purchase requests
            const approvedPRs = response.pageContent.filter(
              (pr: any) => pr.status === 'APPROVED',
            );

            const options = approvedPRs.map((pr: any) => ({
              label: `${pr.series} - ${pr.title || 'Untitled'}`,
              value: pr.id,
            }));
            this.purchaseRequestOptions.set(options);

            // Update form fields with options
            this.updateFormFieldsWithOptions();
          }
        },
        error: (error) => {
          console.error('Error loading purchase request options:', error);
        },
      });
  }

  private loadVendorSkuItems(vendorCode?: string): void {
    if (!vendorCode) {
      return;
    }

    this.vendorSkuService
      .page1(
        undefined,
        vendorCode,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
        0,
        100,
        'skuCode',
        true,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(
        map((response: PageInfoVendorSkuDTO) => {
          return {
            data: response.pageContent || [],
            totalRecords: response.numberOfElements || 0,
            loading: false,
          };
        }),
        catchError((error) => {
          console.error('Error fetching Vendor SKU:', error);
          throw error;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          if (response?.data) {
            this.vendorSKUList.set(response.data);
            const vendorSkuCodes = new Set(
              response.data.map((sku) => sku.skuCode ?? ''),
            );
            setTimeout(() => {
              if (this.isPurchaseRequestMode() && this.purchaseRequestData()) {
                // PR mode: rebuild from original PR items, then filter by vendor SKUs
                const prData = this.purchaseRequestData()!;
                const timestamp = Date.now();
                const itemsFromPr = (prData.items || [])
                  .filter((item) => vendorSkuCodes.has(item.skuCode || ''))
                  .map((item, index) => {
                    const warehouseCode = item.warehouseCode || '';
                    const warehouseName = warehouseCode
                      ? this.getWarehouseName(warehouseCode)
                      : '';
                    const vendorSkuData = response.data.find(
                      (vsku) => vsku.skuCode === item.skuCode,
                    );
                    const unitPrice = Number(vendorSkuData?.unitPrice) || 0;
                    const quantity = item.quantity || 0;
                    return {
                      id: `item-${timestamp}-${index}`,
                      skuCode: item.skuCode || '',
                      skuName: item.skuName || '',
                      spuCode: item.spuCode || '',
                      spuName: item.spuName || '',
                      quantity,
                      unit: 'Cái',
                      unitPrice,
                      tax: 0,
                      totalValue: quantity * unitPrice,
                      vendorSkuCode: vendorSkuData?.vendorSkuCode || '',
                      vendorSkuName: vendorSkuData?.vendorProductName || '',
                      purchaseRequestItem: item.id,
                      warehouseCode,
                      warehouseName,
                    } as PurchaseOrderDetailItem;
                  });
                this.detailList.set(itemsFromPr);
              } else {
                // Normal mode: filter current detailList by vendor SKUs
                const filteredItems = this.detailList().filter((item) =>
                  vendorSkuCodes.has(item.skuCode),
                );
                const updatedItems = filteredItems.map((item) => {
                  const vendorSkuData = this.vendorSKUList().find(
                    (vsku) => vsku.skuCode === item.skuCode,
                  );
                  const unitPrice = Number(vendorSkuData?.unitPrice) || 0;
                  const tax = item.tax || 0;
                  return {
                    ...item,
                    vendorSkuCode: vendorSkuData?.vendorSkuCode || '',
                    vendorSkuName: vendorSkuData?.vendorProductName || '',
                    unitPrice,
                    totalValue: (item.quantity || 0) * unitPrice * (1 + tax / 100),
                  };
                });
                this.detailList.set(updatedItems);
              }
              this.cdr.detectChanges();
            }, 0);
          }
        },
        error: (error) => {
          console.error('Error loading vendor SKU items:', error);
        },
      });
  }

  private loadVendorOptions(): void {
    if (!this.isPurchaseRequestMode()) {
      // Normal mode - load all active vendors
      this.loadAllVendorOptions();
      return;
    }

    // PR mode - get vendors by SKU codes from purchase request data
    const prData = this.purchaseRequestData();
    if (!prData || prData.items?.length === 0) {
      this.vendorOptions.set([]);
      this.updateFormFieldsWithOptions();
      return;
    }

    // Get SKU codes from PR items (not from detailList to avoid timing issues)
    const skuCodes = prData.items.map((item) => item.skuCode).filter(Boolean);

    const totalSkus = skuCodes.length; // Total SKUs in PR

    if (totalSkus === 0) {
      this.vendorOptions.set([]);
      this.updateFormFieldsWithOptions();
      return;
    }

    // Step 1: Get vendors that have these SKUs
    this.vendorService
      .getVendors({
        page: 0,
        size: 100,
        status: 'ACTIVE',
        skuCodes: skuCodes,
        sortBy: 'vendorName',
        ascending: true,
      })
      .pipe(
        switchMap((vendorResponse) => {
          if (!vendorResponse.data || vendorResponse.data.length === 0) {
            return of({ vendors: [], vendorSkuMap: new Map(), totalSkus });
          }

          // Step 2: Get vendor SKUs for each vendor to count matches
          const vendorCodes = vendorResponse.data.map((v: any) => v.code);
          const vendorSkuRequests = vendorCodes.map((vendorCode: string) =>
            this.vendorSkuService
              .page1(
                undefined,
                vendorCode,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                true, // active only
                0,
                100, // Get all SKUs
                'skuCode',
                true,
                'body',
                false,
                { httpHeaderAccept: 'application/json' as any },
              )
              .pipe(
                map((response: any) => ({
                  vendorCode,
                  skuCodes:
                    response.pageContent?.map((sku: any) => sku.skuCode) || [],
                })),
                catchError(() => of({ vendorCode, skuCodes: [] })),
              ),
          );

          return forkJoin(vendorSkuRequests).pipe(
            map((vendorSkuResults) => {
              // Create a map of vendorCode -> matched SKU count
              const vendorSkuMap = new Map<string, number>();
              const skuCodeSet = new Set(skuCodes);

              vendorSkuResults.forEach((result) => {
                const matchedCount = result.skuCodes.filter((skuCode: string) =>
                  skuCodeSet.has(skuCode),
                ).length;
                vendorSkuMap.set(result.vendorCode, matchedCount);
              });

              return { vendors: vendorResponse.data, vendorSkuMap, totalSkus };
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ vendors, vendorSkuMap, totalSkus }) => {
          if (!vendors || vendors.length === 0) {
            this.vendorOptions.set([]);
            this.updateFormFieldsWithOptions();
            return;
          }

          // Step 3: Sort vendors by match count (descending), then by name
          const sortedVendors = vendors.sort((a: any, b: any) => {
            const countA = vendorSkuMap.get(a.code) || 0;
            const countB = vendorSkuMap.get(b.code) || 0;

            if (countB !== countA) {
              return countB - countA; // More matches first
            }

            // If same match count, sort by name
            const nameA = (a.name || a.code || '').toLowerCase();
            const nameB = (b.name || b.code || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });

          // Map to options - format: "Vendor Name (matchedCount/totalSkus SKU)"
          const options = sortedVendors.map((vendor: any) => {
            const matchedCount = vendorSkuMap.get(vendor.code) || 0;
            return {
              label: `${vendor.name || vendor.code} (${matchedCount}/${totalSkus} SKU)`,
              value: vendor.code,
            };
          });

          this.vendorOptions.set(options);
          this.updateFormFieldsWithOptions();

          // Step 4: Auto-select first vendor if none selected
          // Use setTimeout to avoid blocking UI and prevent dropdown from closing
          setTimeout(() => {
            if (
              !this.currentFormData?.vendorCode &&
              options.length > 0 &&
              options[0]?.value
            ) {
              const firstVendorCode = options[0].value;
              // Update currentFormData with vendor selection
              this.currentFormData = {
                ...this.currentFormData,
                vendorCode: firstVendorCode,
              };
              // Update form fields to reflect the selection
              this.updateFormFieldsWithOptions();
              // Trigger vendor SKU load after a short delay to avoid blocking
              setTimeout(() => {
                this.loadVendorSkuItems(firstVendorCode);
                // Trigger change detection to update form
                this.cdr.detectChanges();
              }, 100);
            }
          }, 100);
        },
        error: (error) => {
          console.error('Error loading vendor options:', error);
          this.vendorOptions.set([]);
          this.updateFormFieldsWithOptions();
        },
      });
  }

  private loadAllVendorOptions(): void {
    // Load all active vendors for normal mode
    this.vendorService
      .getVendors({
        page: 0,
        size: 100,
        status: 'ACTIVE',
        sortBy: 'vendorName',
        ascending: true,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.data) {
            const options = response.data.map((vendor: any) => ({
              label: vendor.name || vendor.code,
              value: vendor.code,
            }));
            this.vendorOptions.set(options);
            this.updateFormFieldsWithOptions();
          }
        },
        error: (error) => {
          console.error('Error loading vendor options:', error);
        },
      });
  }

  private loadWarehouseOptionsForTable(): void {
    this.loadAllWarehouseOptionsForTable(1, [])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options: Array<{ label: string; value: string }>) => {
          this.warehouseOptions.set(options);
          this.updateWarehouseColumnOptions();
          if (this.isPurchaseRequestMode() && this.purchaseRequestData()) {
            const currentItems = [...this.detailList()];
            this.detailList.set([]);
            setTimeout(() => {
              this.detailList.set(currentItems);
              this.cdr.detectChanges();
            }, 0);
          }
        },
        error: () => {
          this.warehouseOptions.set([]);
        },
      });
  }

  private loadAllWarehouseOptionsForTable(
    page: number,
    acc: Array<{ label: string; value: string }>,
  ): Observable<Array<{ label: string; value: string }>> {
    return this.warehouseOptionsService
      .getOptionsPage(
        { page, size: 100, searchText: '' },
        { status: true, valueKey: 'code' },
      )
      .pipe(
        switchMap((res) => {
          const merged = [
            ...acc,
            ...res.options.map((opt) => ({
              label: opt.label,
              value: String(opt.value),
            })),
          ];
          const hasMore =
            (typeof res.totalElements === 'number' &&
              merged.length < res.totalElements) ||
            (res.pageContentLength > 0 &&
              (!res.totalElements || merged.length % 100 === 0));
          if (!hasMore || res.pageContentLength === 0) {
            return of(merged);
          }
          return this.loadAllWarehouseOptionsForTable(page + 1, merged);
        }),
      );
  }

  private updateWarehouseColumnOptions(): void {
    // The tableSections computed signal will automatically update with new options
    this.cdr.detectChanges();
  }

  private updateFormFieldsWithOptions(): void {
    const updatedFormFields = this.formFields().map((field) => {
      if (field.controlName === 'purchaseRequest') {
        return {
          ...field,
          options: this.purchaseRequestOptions(),
          disabled: false, // Ensure dropdown is enabled when selecting from dropdown
          readonly: false, // Ensure dropdown is not readonly
        };
      }
      if (field.controlName === 'vendorCode') {
        return {
          ...field,
          options: this.vendorOptions(),
        };
      }
      return field;
    });
    this.formFields.set(updatedFormFields);
  }

  private updatePurchaseRequestFieldForDisplay(
    prData: PurchaseRequestRecord,
  ): void {
    const formattedValue = `${prData.series} - ${prData.title || 'Untitled'}`;

    // Update the current form data with the formatted value
    this.currentFormData = {
      ...this.currentFormData,
      purchaseRequest: formattedValue,
    };

    // Update form field to show formatted text instead of dropdown
    const updatedFormFields = this.formFields().map((field) => {
      if (field.controlName === 'purchaseRequest') {
        return {
          ...field,
          type: 'text' as const,
          disabled: true,
          readonly: true,
          defaultValue: formattedValue,
          style: 'background-color: #f5f5f5; color: #666; cursor: not-allowed;', // Visual indication for disabled field
        };
      }
      return field;
    });

    this.formFields.set(updatedFormFields);
  }

  private initializeForm(): void {
    if (!this.isPurchaseRequestMode()) {
      console.log('@@');
      // Set default delivery date to today for normal mode
      const today = new Date().toISOString().split('T')[0];
      this.currentFormData = {
        series: '',
        vendorCode: '',
        deliveryDate: today,
        description: '',
      };
    }
  }

  // Handle form value changes
  onFormValueChanges(formData: any): void {
    const processedFormData = normalizeDeliveryDateField(formData);
    this.handleVendorCodeChange(processedFormData);
    this.currentFormData = processedFormData;
    this.handlePurchaseRequestChange(processedFormData.purchaseRequest);
  }

  private handleVendorCodeChange(formData: any): void {
    if (
      !this.currentFormData ||
      this.currentFormData.vendorCode === formData.vendorCode
    ) {
      return;
    }

    setTimeout(() => {
      if (this.isPurchaseRequestMode()) {
        this.handleVendorChangeInPRMode(formData.vendorCode);
      } else {
        this.handleVendorChangeInNormalMode(formData.vendorCode);
      }
    }, 0);
  }

  private handleVendorChangeInPRMode(vendorCode: string): void {
    if (vendorCode) {
      setTimeout(() => {
        this.loadVendorSkuItems(vendorCode);
      }, 50);
    } else {
      this.reloadFullPRItems();
      this.vendorSKUList.set([]);
    }
  }

  private handleVendorChangeInNormalMode(vendorCode: string): void {
    if (vendorCode) {
      setTimeout(() => {
        this.loadVendorSkuItems(vendorCode);
      }, 50);
    } else {
      this.vendorSKUList.set([]);
      this.detailList.set([]);
      this.selectedDetails.set([]);
    }
  }

  private handlePurchaseRequestChange(purchaseRequestValue: any): void {
    // The add form emits its initial empty value before the URL-selected PR is
    // loaded. The PR field is read-only in this flow, so that emission must not
    // be treated as the user clearing the selection.
    if (this.isPurchaseRequestLockedFromRoute) {
      return;
    }

    const newPurchaseRequestId = extractPurchaseRequestIdFromOptions(
      purchaseRequestValue,
      this.purchaseRequestOptions(),
      this.purchaseRequestId,
    );

    if (
      shouldSwitchToNormalModePure(
        newPurchaseRequestId,
        this.purchaseRequestId,
        this.isPurchaseRequestMode(),
      )
    ) {
      this.switchToNormalMode();
      return;
    }

    if (
      newPurchaseRequestId !== null &&
      shouldSwitchToPRModePure(newPurchaseRequestId, this.purchaseRequestId)
    ) {
      this.handlePurchaseRequestSelection(newPurchaseRequestId);
    }
  }

  private handlePurchaseRequestSelection(purchaseRequestId: number): void {
    // Don't reload if it's the same PR
    if (this.purchaseRequestId === purchaseRequestId) {
      return;
    }

    this.purchaseRequestId = purchaseRequestId;
    this.isPurchaseRequestLockedFromRoute = false;

    // Switch to PR mode
    this.isPurchaseRequestMode.set(true);
    this.isNormalMode.set(false);

    // Clear existing items before loading new ones
    this.detailList.set([]);

    // Load purchase request data and items
    this.purchaseRequestService
      .getPurchaseRequestById(purchaseRequestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            this.purchaseRequestData.set(response);

            // Update form data with purchase request ID (keep as ID for dropdown to work)
            // Dropdown will display the label from options automatically
            this.currentFormData = {
              ...this.currentFormData,
              purchaseRequest: purchaseRequestId, // Keep as ID for dropdown
              vendorCode: '', // Will be auto-selected by loadVendorOptions
              description: response.description || '',
            };

            // First, update PR options to include the selected PR
            this.updatePurchaseRequestOptionsForForm(response);

            // Load items from PR (this will call loadVendorOptions which will auto-select vendor)
            this.loadItemsFromPurchaseRequest(response);

            // Switch form fields to PR mode, but update purchaseRequest field to be dropdown (not disabled text)
            const prFormFields = PURCHASE_ORDER_ADD_FORM_FIELDS_PR.map(
              (field) => {
                if (field.controlName === 'purchaseRequest') {
                  return {
                    ...field,
                    type: 'searchableDropdown' as const, // Change to dropdown
                    disabled: false, // Enable dropdown
                    readonly: false, // Not readonly
                    options: this.purchaseRequestOptions(), // Set options
                    showClear: true, // Allow clearing
                  };
                }
                return field;
              },
            );
            this.formFields.set(prFormFields);
            this.loadAndApplyPurchaseOrderConfig();

            // Update form fields with PR options and ensure dropdown is enabled
            this.updateFormFieldsWithOptions();

            // Trigger change detection to update form
            setTimeout(() => {
              this.cdr.detectChanges();
            }, 0);
          }
        },
        error: (error) => {
          console.error('Error loading purchase request:', error);
          this.messageService.add({
            severity: 'error',
            summary: String(
              this.translateService.instant('common.messages.error'),
            ),
            detail: this.translateService.instant(
              'purchase_order.add.messages.failed_to_load_purchase_request',
            ),
          });
          // Revert to normal mode on error
          this.switchToNormalMode();
        },
      });
  }

  private updatePurchaseRequestOptionsForForm(
    prData: PurchaseRequestRecord,
  ): void {
    // Add the selected PR to options if not already there
    const currentOptions = this.purchaseRequestOptions();
    const prOption = {
      label: `${prData.series} - ${prData.title || 'Untitled'}`,
      value: prData.id,
    };

    // Check if option already exists
    const exists = currentOptions.some((opt) => opt.value === prData.id);
    if (!exists) {
      this.purchaseRequestOptions.set([...currentOptions, prOption]);
    } else {
      // Update existing option label in case it changed
      const updatedOptions = currentOptions.map((opt) =>
        opt.value === prData.id ? prOption : opt,
      );
      this.purchaseRequestOptions.set(updatedOptions);
    }
  }

  private switchToNormalMode(): void {
    this.isPurchaseRequestMode.set(false);
    this.isNormalMode.set(true);
    this.purchaseRequestId = null;
    this.isPurchaseRequestLockedFromRoute = false;
    this.purchaseRequestData.set(null);

    // Clear all data: vendor, products, selections
    this.detailList.set([]);
    this.selectedDetails.set([]);
    this.vendorSKUList.set([]);
    this.vendorOptions.set([]);

    // Reset form data - clear vendor and PR
    const today = new Date().toISOString().split('T')[0];
    this.currentFormData = {
      ...this.currentFormData,
      purchaseRequest: null,
      vendorCode: '', // Clear vendor selection
      deliveryDate: today,
    };

    // Reload all vendor options for normal mode
    this.loadAllVendorOptions();

    // Reload purchase request options for normal mode
    this.loadPurchaseRequestOptions();

    // Update form fields to normal mode
    this.formFields.set(PURCHASE_ORDER_ADD_FORM_FIELDS);
    this.loadAndApplyPurchaseOrderConfig();

    // Update form fields with options
    this.updateFormFieldsWithOptions();

    // Trigger change detection to show add/delete buttons
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  // Handle form actions (save, cancel)
  onFormAction(action: string): void {
    switch (action) {
      case 'save':
        this.onSave();
        break;
      case 'cancel':
        this.onCancel();
        break;
    }
  }

  // Handle table actions (add, edit, delete detail items)
  onTableAction(event: { action: string; data?: any; section?: string }): void {
    switch (event.action) {
      case 'addDetail':
        this.onAddDetail();
        break;
      case 'deleteDetail':
        this.onDeleteDetail(event.data);
        break;
      case 'deleteSelectedDetail':
        this.onDeleteSelectedDetails();
        break;
      case 'cellValueChange':
        this.onCellValueChange(event.data);
        break;
      case 'cellInputChange':
        this.onUnitPriceInputChange(event.data);
        break;
    }
  }

  onCellValueChange(event: { rowData: any; prop: string; value: any }): void {
    if (
      event.prop === 'unitPrice' ||
      event.prop === 'tax' ||
      event.prop === 'quantity'
    ) {
      this.updateItemTotalValue(event.rowData, event.prop, event.value);
    } else if (event.prop === 'warehouseCode') {
      const warehouseName = this.getWarehouseName(event.value);

      this.detailList.update((items) =>
        items.map((item) =>
          item.id === event.rowData.id
            ? { ...item, warehouseCode: event.value, warehouseName }
            : item,
        ),
      );
    }
  }

  private updateItemTotalValue(
    item: PurchaseOrderDetailItem,
    prop: string,
    value: any,
  ): void {
    const items = this.detailList();
    const itemIndex = items.findIndex((i) => i.id === item.id);

    if (itemIndex !== -1) {
      const updatedItem = { ...items[itemIndex] };

      if (prop === 'unitPrice') {
        updatedItem.unitPrice = Number(value) || 0;
      } else if (prop === 'tax') {
        updatedItem.tax = Math.max(0, Math.min(100, Number(value) || 0));
      } else if (prop === 'quantity') {
        updatedItem.quantity = Math.max(1, Number(value) || 1);
      }

      // Same as PO Edit: totalValue = quantity * unitPrice * (1 + tax/100)
      const quantity = updatedItem.quantity;
      const unitPrice = updatedItem.unitPrice;
      const tax = updatedItem.tax ?? 0;
      updatedItem.totalValue = quantity * unitPrice * (1 + tax / 100);

      // Update the item in the list
      const updatedItems = [...items];
      updatedItems[itemIndex] = updatedItem;
      this.detailList.set(updatedItems);
    }
  }

  // Realtime input change handler for unit price
  private onUnitPriceInputChange(event: {
    rowData: any;
    prop: string;
    value: any;
  }): void {
    if (event.prop !== 'unitPrice') return;
    const id = event.rowData?.id;
    const numeric = Number(event.value ?? 0) || 0;
    if (id) {
      this.unitPriceInput$.next({ id, value: numeric });
    }
  }

  // Exposed to table for per-cell error message key
  getDetailFieldError(rowIndex: number, fieldName: string): string | null {
    const items = this.detailList();
    const item = items[rowIndex];
    if (!item) return null;

    if (fieldName === 'unitPrice') {
      const isInvalid = this.invalidUnitPriceIds.has(item.id);
      return isInvalid
        ? 'purchase_order.add.validation.unit_price_min_tooltip'
        : null;
    }

    return null;
  }

  // Add new detail item (Normal mode only; PR mode has no add button)
  onAddDetail(): void {
    if (this.isNormalMode()) {
      this.showSkuSelectionDialog();
    }
  }

  // Show SKU selection dialog for normal mode
  private showSkuSelectionDialog(): void {
    this.editingSkuItem.set(null);
    this.showSkuDialog.set(true);
  }

  // Delete detail item
  onDeleteDetail(item: PurchaseOrderDetailItem): void {
    const currentItems = this.detailList();
    const updatedItems = currentItems.filter((i) => i.id !== item.id);
    this.detailList.set(updatedItems);

    this.messageService.add({
      severity: 'success',
      summary:
        String(this.translateService.instant('common.messages.success')) ||
        'Success',
      detail:
        String(
          this.translateService.instant(
            'purchase_order.add.messages.item_deleted',
          ),
        ) || 'Item deleted successfully',
    });
  }

  // Delete selected detail items
  onDeleteSelectedDetails(): void {
    const selectedItems = this.selectedDetails();
    if (selectedItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary:
          String(this.translateService.instant('common.messages.warning')) ||
          'Warning',
        detail:
          String(
            this.translateService.instant(
              'purchase_order.add.messages.no_items_selected',
            ),
          ) || 'No items selected for deletion',
      });
      return;
    }

    const currentItems = this.detailList();
    const selectedIds = selectedItems.map((item) => item.id);
    const updatedItems = currentItems.filter(
      (item) => !selectedIds.includes(item.id),
    );

    this.detailList.set(updatedItems);
    this.selectedDetails.set([]);
  }

  // Handle SKU dialog confirmation for normal mode
  onSkuDialogConfirm(skuItem: PurchaseRequestItem): void {
    // Get system SKU info for mapping
    const systemSku = this.getSystemSkuInfo(skuItem.skuCode);

    // Get vendor SKU info for vendorSkuCode and vendorSkuName
    const vendorSku = this.vendorSKUList().find(
      (vsku) => vsku.skuCode === skuItem.skuCode,
    );

    const unitPrice = Number(vendorSku?.unitPrice) || 0;
    const quantity = skuItem.quantity || 0;

    // Convert PurchaseRequestItem to PurchaseOrderDetailItem
    const detailItem: PurchaseOrderDetailItem = {
      id: this.generateId(),
      skuCode: skuItem.skuCode,
      skuName: skuItem.skuName || systemSku?.skuName || '',
      spuCode: systemSku?.spuCode || skuItem.spuCode || '',
      spuName: systemSku?.spuName || skuItem.spuName || '',
      quantity,
      unit: 'Cái', // Default unit
      unitPrice, // Prefill from vendor–SKU contracted price
      tax: 0, // Default tax percentage
      totalValue: quantity * unitPrice,
      vendorSkuCode: vendorSku?.vendorSkuCode || '',
      vendorSkuName: vendorSku?.vendorProductName || '',
    };

    // Add to detail list
    const currentItems = this.detailList();
    this.detailList.set([...currentItems, detailItem]);
  }

  // Generate unique ID for items
  private generateId(): string {
    return (
      'detail_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    );
  }

  // Save purchase order
  onSave(): void {
    if (!this.validateAllUnitPricesBeforeSave()) {
      return;
    }
    if (this.validateForm()) {
      this.loading.set(true);

      if (this.isPurchaseRequestMode()) {
        this.savePurchaseOrderFromRequest();
      } else {
        this.savePurchaseOrderNormal();
      }
    }
  }

  private savePurchaseOrderFromRequest(): void {
    const items = this.detailList().map((item) => ({
      purchaseRequestItem: item.purchaseRequestItem,
      skuCode: item.skuCode,
      price: item.unitPrice,
      tax: item.tax / 100, // Convert percentage to decimal (e.g., 10% -> 0.1)
      quantity: item.quantity,
      warehouseCode: item.warehouseCode || '',
      vendorSkuCode: item.vendorSkuCode || '',
    }));

    const purchaseOrderData: PurchaseOrderDTO = {
      purchaseRequest: this.purchaseRequestId!,
      series: this.currentFormData.series,
      vendorCode: this.currentFormData.vendorCode,
      deliveryDate: this.currentFormData.deliveryDate,
      description: this.currentFormData.description,
      items: items,
    };

    // Call the actual API
    this.purchaseOrderService
      .createPurchaseOrder(purchaseOrderData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary:
              String(
                this.translateService.instant('common.messages.success'),
              ) || 'Success',
            detail:
              String(
                this.translateService.instant(
                  'purchase_order.add.messages.order_saved',
                ),
              ) || 'Purchase order saved successfully',
            life: 3000,
          });

          // Navigate back to purchase order list after delay to allow toast to display
          setTimeout(() => {
            this.router.navigate(['/procurement/purchase-order']);
          }, 1000);
        },
        error: (error) => {
          this.loading.set(false);
          this.handleApiError(error);
        },
      });
  }

  private savePurchaseOrderNormal(): void {
    const items = this.detailList().map(
      (item) =>
        ({
          skuCode: item.skuCode,
          price: item.unitPrice,
          tax: item.tax / 100, // Convert percentage to decimal (e.g., 10% -> 0.1)
          quantity: item.quantity,
          warehouseCode: item.warehouseCode || '',
          vendorSkuCode: item.vendorSkuCode || '',
        }) as PurchaseOrderItemDTO,
    );

    const purchaseOrderData: PurchaseOrderDTO = {
      purchaseRequest: this.currentFormData.purchaseRequest,
      series: this.currentFormData.series,
      vendorCode: this.currentFormData.vendorCode,
      deliveryDate: this.currentFormData.deliveryDate,
      description: this.currentFormData.description,
      items: items,
    };

    // Call the API
    this.purchaseOrderService
      .createPurchaseOrder(purchaseOrderData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary:
              String(
                this.translateService.instant('common.messages.success'),
              ) || 'Success',
            detail:
              String(
                this.translateService.instant(
                  'purchase_order.add.messages.order_saved',
                ),
              ) || 'Purchase order saved successfully',
            life: 3000,
          });

          // Navigate back to purchase order list after delay to allow toast to display
          setTimeout(() => {
            this.router.navigate(['/procurement/purchase-order']);
          }, 1000);
        },
        error: (error) => {
          this.loading.set(false);
          this.handleApiError(error);
        },
      });
  }

  // Cancel and go back
  onCancel(): void {
    this.router.navigate(['/procurement/purchase-order']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private handleApiError(error: any): void {
    const classified = classifyPurchaseOrderSaveError(error);
    const summary =
      String(this.translateService.instant('common.messages.error')) || 'Error';

    if (classified.kind === 'duplicate_series') {
      const seriesLabel = classified.seriesNumber || 'N/A';
      this.messageService.add({
        severity: 'error',
        summary,
        detail: String(
          this.translateService.instant(
            'purchase_order.add.messages.series_already_exists',
            { series: seriesLabel },
          ),
        ),
      });
      return;
    }

    if (classified.kind === 'delivery_date') {
      this.messageService.add({
        severity: 'error',
        summary,
        detail: String(
          this.translateService.instant(
            'purchase_order.add.messages.delivery_date_must_be_future_or_present',
          ),
        ),
      });
      return;
    }

    this.messageService.add({
      severity: 'error',
      summary,
      detail:
        String(
          this.translateService.instant(
            'purchase_order.add.messages.failed_to_save',
          ),
        ) || 'Failed to save purchase order',
    });
  }

  private validateForm(): boolean {
    const issue = evaluatePurchaseOrderAddValidation({
      formData: this.currentFormData,
      detailItems: this.detailList(),
      formFields: this.formFields(),
      isPurchaseRequestMode: this.isPurchaseRequestMode(),
    });

    if (!issue) {
      return true;
    }

    if (issue.kind === 'missing_required') {
      const validationKey = this.isPurchaseRequestMode()
        ? 'purchase_order.add.validation.pr_mode_form_required'
        : 'purchase_order.add.validation.main_form_required';

      this.messageService.add({
        severity: 'error',
        summary: String(this.translateService.instant('common.messages.error')),
        detail: String(this.translateService.instant(validationKey)),
      });
      return false;
    }

    if (issue.kind === 'price_required') {
      this.messageService.add({
        severity: 'error',
        summary: String(
          this.translateService.instant('common.messages.error'),
        ),
        detail: String(
          this.translateService.instant(
            'purchase_order.add.validation.price_required',
          ),
        ),
      });
      return false;
    }

    this.messageService.add({
      severity: 'error',
      summary: String(this.translateService.instant('common.messages.error')),
      detail: String(
        this.translateService.instant(
          'purchase_order.add.validation.detail_items_required',
        ),
      ),
    });
    return false;
  }

  private validateAllUnitPricesBeforeSave(): boolean {
    if (!hasDetailUnitPriceBelowMinimum(this.detailList())) {
      return true;
    }

    this.messageService.add({
      severity: 'error',
      summary: String(this.translateService.instant('common.messages.error')),
      detail: String(
        this.translateService.instant(
          'purchase_order.add.validation.unit_price_min_toast',
        ),
      ),
    });
    return false;
  }

  // Format currency for display
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(value);
  }

  // Calculate total value
  getTotalValue(): number {
    return this.detailList().reduce(
      (total, item) => total + item.totalValue,
      0,
    );
  }

}
